import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { qrtFunnel } from "../../apis/qrtAdmin";
import { StatCard, Spinner, fmtDateTime, num } from "./qrtUi";

const EVENT_LABELS = {
  connect_wa_clicked: "Connect clicked",
  connect_wa_success: "Connected",
  connect_wa_failed: "Connect failed",
  connect_wa_blocked: "Connect blocked (no plan)",
  plans_viewed: "Plans viewed",
  payment_started: "Payments started",
  payment_success: "Payments completed",
  payment_failed: "Payments failed",
  payment_abandoned: "Payments abandoned",
};

const daysAgoIso = (n) => new Date(Date.now() - n * 24 * 3600 * 1000).toISOString().slice(0, 10);

const QrtFunnel = () => {
  const [data, setData] = useState(null);
  const [from, setFrom] = useState(daysAgoIso(29));
  const [to, setTo] = useState(daysAgoIso(0));
  const [eventType, setEventType] = useState("");
  const [userId, setUserId] = useState("");

  const load = useCallback(() => {
    qrtFunnel({
      from, to,
      ...(eventType ? { event_type: eventType } : {}),
      ...(userId ? { user_id: userId } : {}),
    })
      .then((res) => setData(res.data?.data || null))
      .catch(() => toast.error("Failed to load funnel"));
  }, [from, to, eventType, userId]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <Spinner />;

  const s = data.summary || {};
  const count = (k) => s[k]?.total || 0;
  const started = count("payment_started");
  const paid = count("payment_success");
  const abandoned = count("payment_abandoned");
  const clicked = count("connect_wa_clicked");
  const connected = count("connect_wa_success");

  const pct = (a, b) => (b > 0 ? `${Math.round((a / b) * 100)}%` : "—");

  const parseMeta = (m) => {
    if (!m) return null;
    try {
      return typeof m === "string" ? JSON.parse(m) : m;
    } catch {
      return null;
    }
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h2>Conversion funnel</h2>
          <p>Connect attempts and payment drop-offs, {data.range.from} → {data.range.to}</p>
        </div>
        <div className="grow" />
        <div className="qrt-inline">
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          <input
            type="text" placeholder="User ID" inputMode="numeric" style={{ width: 90 }}
            value={userId} onChange={(e) => setUserId(e.target.value.replace(/\D/g, ""))}
          />
        </div>
      </div>

      <div className="qrt-stats">
        <StatCard label="Connect clicked" value={num(clicked)} sub={`${num(s.connect_wa_clicked?.users || 0)} users`} />
        <StatCard label="Connected" value={num(connected)} sub={`${pct(connected, clicked)} of clicks`} tone={connected > 0 ? "g" : ""} />
        <StatCard label="Blocked (no plan)" value={num(count("connect_wa_blocked"))} />
        <StatCard label="Plans viewed" value={num(count("plans_viewed"))} sub={`${num(s.plans_viewed?.users || 0)} users`} />
        <StatCard label="Payments started" value={num(started)} />
        <StatCard label="Payments completed" value={num(paid)} sub={`${pct(paid, started)} conversion`} tone="g" />
        <StatCard label="Payments abandoned" value={num(abandoned)} sub={`${pct(abandoned, started)} of started`} tone={abandoned > 0 ? "r" : ""} />
      </div>

      <div className="qrt-grid2" style={{ marginTop: 12 }}>
        <div className="qrt-card">
          <div className="qrt-card-head"><h3>Clicked connect, never connected</h3></div>
          <div className="qrt-card-body" style={{ overflowX: "auto" }}>
            {data.connect_dropoffs.length === 0 ? (
              <p className="qrt-empty">No drop-offs in this range.</p>
            ) : (
              <table className="qrt-table">
                <thead><tr><th>User</th><th>Attempts</th><th>Last attempt</th></tr></thead>
                <tbody>
                  {data.connect_dropoffs.map((row) => (
                    <tr key={`c-${row.user_id}`}>
                      <td>
                        <b>{row.user_name || `User #${row.user_id}`}</b>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{row.user_email}</div>
                      </td>
                      <td>{num(row.attempts)}</td>
                      <td>{fmtDateTime(row.last_attempt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        <div className="qrt-card">
          <div className="qrt-card-head"><h3>Started payment, never paid</h3></div>
          <div className="qrt-card-body" style={{ overflowX: "auto" }}>
            {data.payment_dropoffs.length === 0 ? (
              <p className="qrt-empty">No abandoned payments in this range.</p>
            ) : (
              <table className="qrt-table">
                <thead><tr><th>User</th><th>Attempts</th><th>Last attempt</th></tr></thead>
                <tbody>
                  {data.payment_dropoffs.map((row) => (
                    <tr key={`p-${row.user_id}`}>
                      <td>
                        <b>{row.user_name || `User #${row.user_id}`}</b>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{row.user_email}</div>
                      </td>
                      <td>{num(row.attempts)}</td>
                      <td>{fmtDateTime(row.last_attempt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      <div className="qrt-card" style={{ marginTop: 12 }}>
        <div className="qrt-card-head">
          <h3>Recent events</h3>
          <select value={eventType} onChange={(e) => setEventType(e.target.value)}>
            <option value="">All events</option>
            {Object.entries(EVENT_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className="qrt-card-body" style={{ overflowX: "auto" }}>
          {data.recent_events.length === 0 ? (
            <p className="qrt-empty">No events in this range.</p>
          ) : (
            <table className="qrt-table">
              <thead><tr><th>When</th><th>User</th><th>Event</th><th>Details</th></tr></thead>
              <tbody>
                {data.recent_events.map((ev) => {
                  const meta = parseMeta(ev.metadata);
                  return (
                    <tr key={ev.id}>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtDateTime(ev.created_at)}</td>
                      <td>
                        <b>{ev.user_name || `User #${ev.user_id}`}</b>
                        <div style={{ fontSize: 11.5, color: "var(--text-3)" }}>{ev.user_email}</div>
                      </td>
                      <td>{EVENT_LABELS[ev.event_type] || ev.event_type}</td>
                      <td style={{ fontSize: 12, color: "var(--text-2)" }}>
                        {meta
                          ? Object.entries(meta)
                              .filter(([k]) => k !== "order_id")
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(" · ")
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default QrtFunnel;
